import React, {useContext, useId} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import classNames from 'classnames';

import type {Post} from '@mattermost/types/posts';
import OverlayTrigger from 'components/overlay_trigger';
import PostContext from 'components/post_view/post_context';
import MenuWrapper from 'components/widgets/menu/menu_wrapper';
import MoreIcon from 'components/widgets/icons/more_icon';
import Menu from 'components/widgets/menu/menu';
import Tooltip from 'components/tooltip';
import {getIsMobileView} from 'selectors/views/browser';
import {useAppSelector} from 'stores/redux_store';
import Constants, {Locations} from 'utils/constants';

import {type PostMenuItem, type UsePostMenuItemsParams} from '../types';
import {usePostMenuItems} from '../use_post_menu_items';
import {usePostMenuShortcuts} from '../use_post_menu_shortcuts';

type DotMenuProps = Omit<UsePostMenuItemsParams, 'triggerType' | 'location' | 'onLogPostAction'> & {
    isMenuOpen: boolean;
    onDropdownOpened: (open: boolean) => void;
}

type DotMenuDropdownProps = DotMenuProps & {menuId: string};

const tooltip = (
    <Tooltip
        id='dotmenu-icon-tooltip'
        className='hidden-xs'
    >
        <FormattedMessage
            id='post_info.dot_menu.tooltip.more_actions'
            defaultMessage='More'
        />
    </Tooltip>
);

function renderMenuItemAction(menuItem: PostMenuItem | null) {
    if (!menuItem) {
        return null;
    }

    return (
        <Menu.ItemAction
            key={menuItem.attributes.id}
            id={menuItem.attributes.id}
            text={menuItem.text}
            icon={menuItem.icon}
            onClick={menuItem.action}
            isDangerous={menuItem.isDangerous}
            rightDecorator={menuItem.shortcut}
            dataQa={menuItem.attributes['data-qa']}
        />
    );
}

function renderDivider(post: Post, suffix: string): React.ReactNode {
    const id = `divider_post_${post.id}_${suffix}`;
    return (
        <li
            key={id}
            id={id}
            className='MenuItem__divider'
            role='menuitem'
        />
    );
}

function stopEventPropagation(e: React.MouseEvent) {
    e.stopPropagation();
}

function MenuDropdown({
    post,
    menuId,
    isMenuOpen,
    onDropdownOpened,
    ...restProps
}: DotMenuDropdownProps) {
    const {formatMessage} = useIntl();
    const isMobile = useAppSelector(getIsMobileView);
    const {location, onLogPostAction} = useContext(PostContext);

    const menuItems = usePostMenuItems({
        ...restProps,
        post,
        location,
        triggerType: 'dots',
        onLogPostAction,
    });

    usePostMenuShortcuts(menuItems, isMenuOpen, () => onDropdownOpened(false));

    const isActivityFeedLocation = location === Locations.ACTIVITY_FEED;

    const renderActivityFeedMenuItems = () => {
        return [
            renderMenuItemAction(menuItems.REACTIONS),
            renderMenuItemAction(menuItems.SAVE),
            renderMenuItemAction(menuItems.REMOVE_FROM_SAVED),
            menuItems.COPY_LINK && renderDivider(post, 'copy-link'),
            renderMenuItemAction(menuItems.COPY_LINK),
            renderMenuItemAction(menuItems.COPY_TEXT),
            (menuItems.EDIT || menuItems.DELETE) && renderDivider(post, 'edit-delete'),
            renderMenuItemAction(menuItems.EDIT),
            renderMenuItemAction(menuItems.DELETE),

        ];
    };

    const renderPostMenuItems = () => {
        return [
            isMobile && renderMenuItemAction(menuItems.ADD_EMOJI),
            renderMenuItemAction(menuItems.REPLY),
            menuItems.REPLY && renderDivider(post, 'reply'),
            renderMenuItemAction(menuItems.DISCUSS_MESSAGE),
            renderMenuItemAction(menuItems.POST_FOLLOW),
            renderMenuItemAction(menuItems.MARK_AS_UNREAD),
            isMobile && renderMenuItemAction(menuItems.SAVE),
            isMobile && renderMenuItemAction(menuItems.REMOVE_FROM_SAVED),
            renderMenuItemAction(menuItems.PIN),
            renderMenuItemAction(menuItems.UNPIN),
            renderMenuItemAction(menuItems.REACTIONS),
            renderMenuItemAction(menuItems.COPY_LINK),
            menuItems.COPY_LINK && (menuItems.EDIT || menuItems.DELETE) && renderDivider(post, 'edit'),
            renderMenuItemAction(menuItems.COPY_TEXT),
            renderMenuItemAction(menuItems.EDIT),
            renderMenuItemAction(menuItems.DELETE),
        ];
    };

    return (
        <Menu
            id={menuId}
            openLeft={true}
            ariaLabel={formatMessage({id: 'post_info.menuAriaLabel', defaultMessage: 'Post extra options'})}
            customStyles={{overflowY: 'auto', position: 'relative', left: 'inherit', right: 0}}
            onContextMenu={stopEventPropagation}
        >
            {isActivityFeedLocation ? renderActivityFeedMenuItems() : renderPostMenuItems()}
        </Menu>
    );
}

export function DotMenu(props: DotMenuProps) {
    const {onDropdownOpened, isMenuOpen} = props;
    const menuId = useId();
    const {formatMessage} = useIntl();

    return (
        <MenuWrapper
            placement='right'
            onToggle={onDropdownOpened}
        >
            <OverlayTrigger
                className='hidden-xs'
                delayShow={Constants.OVERLAY_TIME_DELAY}
                placement='top'
                overlay={tooltip}
                rootClose={true}
            >
                <button
                    id={menuId}
                    aria-label={formatMessage({
                        id: 'post_info.dot_menu.tooltip.more_actions',
                        defaultMessage: 'Actions',
                    }).toLowerCase()}
                    className={classNames('post-menu__item', {
                        'post-menu__item--active': isMenuOpen,
                    })}
                    type='button'
                    aria-expanded='false'
                >
                    <MoreIcon size={12} />
                </button>
            </OverlayTrigger>
            {isMenuOpen && <MenuDropdown {...props} menuId={menuId} />}
        </MenuWrapper>
    );
}
